<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { toast } from 'vue-sonner';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { playerNameFor } from '@/composables/api/usePlayerSummaries';
import { scheduleWriteErrorMessage, useDeleteSchedule } from '@/composables/api/useScheduleMutations';
import { summarizeSchedule, summaryText, type ScheduleRow } from '@/utils/customPlaylistSchedule';
import PlaylistContainerZoneLine from '@/components/DataTable/PlaylistContainerZoneLine.vue';
import { zoneLineRowFor } from './zoneLineRow';

/**
 * Confirm and run a schedule delete (decision D8). The endpoint hard-deletes
 * the row (forceDelete), so the wording says it cannot be undone. The dialog
 * stays open while the request runs and shows the error when it fails, which
 * ConfirmDialog.vue cannot do.
 */
const props = defineProps<{
    schedule: ScheduleRow | null;
    listId: number;
    /** The schedule's zone, drawn as its linked zone line under the summary. */
    zoneId?: number | null;
    /** The zone's name when the caller already holds it, used before the zone lookup answers. */
    zoneName?: string | null;
}>();

const open = defineModel<boolean>('open', { default: false });

const emit = defineEmits<{
    (event: 'deleted', scheduleId: number): void;
}>();

const deleteMutation = useDeleteSchedule();
const errorMessage = ref<string | null>(null);

/**
 * Bumped every time the dialog opens. A delete that comes back after the
 * dialog was reopened, possibly on another schedule, only shows its toast: it
 * must not close that dialog, report that schedule deleted, or show its error.
 */
const openCount = ref(0);
/** The opening whose delete is in flight, so only that opening shows "Deleting…". */
const deletingFor = ref<number | null>(null);
const isDeleting = computed(() => deletingFor.value !== null && deletingFor.value === openCount.value);

watch(
    open,
    (isOpen) => {
        if (!isOpen) return;
        openCount.value += 1;
        errorMessage.value = null;
    },
    { immediate: true },
);

const description = computed(() => {
    if (!props.schedule) return null;
    const text = summaryText(summarizeSchedule(props.schedule, { now: new Date(), playerName: playerNameFor }));
    return text || null;
});

const zoneRow = computed(() => (open.value ? zoneLineRowFor(props.zoneId, props.zoneName ?? null) : null));

async function confirmDelete(): Promise<void> {
    const scheduleId = Number(props.schedule?.id);
    if (!Number.isInteger(scheduleId) || scheduleId <= 0) return;
    errorMessage.value = null;
    const thisOpening = openCount.value;
    const stillShowing = () => open.value && openCount.value === thisOpening;
    deletingFor.value = thisOpening;
    try {
        await deleteMutation.mutateAsync({ listId: props.listId, scheduleId });
        toast.success('Schedule deleted.');
        if (!stillShowing()) return;
        emit('deleted', scheduleId);
        open.value = false;
    } catch (error) {
        const message = scheduleWriteErrorMessage(error, "Couldn't delete the schedule.");
        toast.error("Couldn't delete the schedule", { description: message });
        if (!stillShowing()) return;
        errorMessage.value = message;
    } finally {
        if (deletingFor.value === thisOpening) deletingFor.value = null;
    }
}
</script>

<template>
    <Dialog v-model:open="open">
        <DialogContent data-testid="schedule-delete-dialog">
            <DialogHeader>
                <DialogTitle>Delete this schedule?</DialogTitle>
                <DialogDescription>This cannot be undone.</DialogDescription>
            </DialogHeader>
            <div v-if="description || zoneRow" class="flex min-w-0 flex-col gap-1 text-sm">
                <p v-if="description" data-testid="schedule-delete-summary">{{ description }}</p>
                <PlaylistContainerZoneLine v-if="zoneRow" :row="zoneRow" class="min-w-0" data-testid="schedule-delete-zone" />
            </div>
            <p v-if="errorMessage" class="text-destructive text-sm" role="alert" data-testid="schedule-delete-error">{{ errorMessage }}</p>
            <DialogFooter>
                <DialogClose as-child>
                    <Button variant="outline" :disabled="isDeleting">Cancel</Button>
                </DialogClose>
                <Button
                    variant="destructive"
                    :disabled="isDeleting"
                    data-testid="schedule-delete-confirm"
                    @click="confirmDelete"
                >
                    {{ isDeleting ? 'Deleting…' : 'Delete' }}
                </Button>
            </DialogFooter>
        </DialogContent>
    </Dialog>
</template>
